import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Upload, 
  Plus, 
  Save, 
  Brain, 
  CheckCircle,
  AlertTriangle,
  Calendar,

} from 'lucide-react';
import {
  Contract,
  ContractSummary,
  ReviewClause,
  AnalysisDocument,

  ClauseCategory,
  ClauseStatus,
  Priority
} from '../../../types/contract.types';
import { decodeHtmlEntities } from '../../../utils/textUtils';

interface ContractReviewPageProps {
  contractId: string;
  contract: Contract | undefined;
  onUpdateContract: (id: string, updates: Partial<Contract>) => void;
}

interface ExtendedContractSummary extends ContractSummary {
  executiveSummary: string;
  contractValue: number;
  contractDuration: string;
  parties: string[];
  mainObligations: string[];
  criticalDates: string[];
  recommendations: string[];
}

interface ExtendedReviewClause extends ReviewClause {
  currentStatus: ClauseStatus;
  clauseText: string;
  proposedChanges: string;
  riskLevel: Priority;
  notes: string;
  isAutoExtracted: boolean;
}

const ContractReviewPage: React.FC<ContractReviewPageProps> = ({ 
  contractId, 
  contract, 
  onUpdateContract 
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upload' | 'summary' | 'clauses' | 'negotiation'>('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [clauses, setClauses] = useState<ExtendedReviewClause[]>([]);
  
  // Form states with extended types
  const [summaryForm, setSummaryForm] = useState<ExtendedContractSummary>({
    executiveSummary: '',
    contractValue: 0,
    contractDuration: '',
    parties: [],
    mainObligations: [],
    criticalDates: [],
    recommendations: [],
    generatedSummary: '',
    keyFindings: [],
    riskLevel: 'medium',
    createdDate: new Date(),
    createdBy: 'Current User'
  });

  const [newClauseForm, setNewClauseForm] = useState<Partial<ExtendedReviewClause>>({
    clauseTitle: '',
    clauseText: '',
    category: 'Terms',
    currentStatus: 'pending',
    priority: 'medium',
    proposedChanges: '',
    riskLevel: 'medium',
    notes: '',
    isAutoExtracted: false
  });

  const [showAddClauseForm, setShowAddClauseForm] = useState(false);

  // File upload handler
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/markdown'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload a PDF, Word document, text file, or markdown file.');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB.');
        return;
      }
      
      setUploadedFile(file);
    }
  }, []);

  // Process uploaded file
  const processUploadedFile = async () => {
    if (!uploadedFile || !contract) return;

    setIsProcessing(true);
    setProcessingProgress(0);

    try {
      // Read file content
      const fileContent = await readFileContent(uploadedFile);
      
      // Simulate progress
      setProcessingProgress(20);

      // Import Claude service
      const { analyzeContractWithClaude } = await import('../../../services/claude/claudeService');
      
      // Process with Claude
      const analysisResult = await analyzeContractWithClaude(
        uploadedFile,
        contractId,
        {
          contractName: decodeHtmlEntities(contract.contractTitle),
          supplier: contract.supplier,
          analysisDocument: fileContent
        },
        (progress: number) => {
          setProcessingProgress(20 + (progress * 0.6)); // 20-80%
        }
      );

      setProcessingProgress(80);

      // Process the results
      const processedData = processAnalysisResults(analysisResult);
      
      // Update states
      setSummaryForm(processedData.summary);
      setClauses(processedData.clauses);
      
      // Save the analysis document reference
      const analysisDoc: AnalysisDocument = {
        fileName: uploadedFile.name,
        fileUrl: URL.createObjectURL(uploadedFile),
        fileSize: uploadedFile.size,
        uploadDate: new Date(),
        uploadedBy: 'Current User'
      };

      // Update contract with analysis document
      onUpdateContract(contractId, {
        analysisDocument: analysisDoc,
        contractSummary: {
          generatedSummary: processedData.summary.executiveSummary,
          keyFindings: processedData.summary.keyFindings,
          riskLevel: processedData.summary.riskLevel,
          createdDate: new Date(),
          createdBy: 'Current User'
        }
      });

      setProcessingProgress(100);
      
      // Switch to summary tab after processing
      setTimeout(() => {
        setActiveTab('summary');
        setIsProcessing(false);
        setProcessingProgress(0);
      }, 500);

    } catch (error) {
      console.error('Error processing file:', error);
      
      // More detailed error message
      let errorMessage = 'Error processing file. ';
      if (error instanceof Error) {
        errorMessage += error.message;
        
        // Check for specific error types
        if (error.message.includes('API key')) {
          errorMessage = 'Claude API key is missing or invalid. Please check your .env file.';
        } else if (error.message.includes('rate limit')) {
          errorMessage = 'API rate limit exceeded. Please try again later.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Please check your internet connection.';
        }
      }
      
      alert(errorMessage);
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  };

  // Read file content
  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  // Process analysis results from Claude
  const processAnalysisResults = (claudeAnalysis: any): { summary: ExtendedContractSummary; clauses: ExtendedReviewClause[] } => {
    // Extract summary information
    const summary: ExtendedContractSummary = {
      executiveSummary: claudeAnalysis.summary || '',
      keyFindings: claudeAnalysis.keyTerms || [],
      riskLevel: claudeAnalysis.riskAssessment?.overallRisk || 'medium',
      contractValue: extractContractValue(claudeAnalysis),
      contractDuration: extractDuration(claudeAnalysis),
      parties: extractParties(claudeAnalysis),
      mainObligations: extractObligations(claudeAnalysis),
      criticalDates: extractCriticalDates(claudeAnalysis),
      recommendations: claudeAnalysis.recommendations || [],
      generatedSummary: claudeAnalysis.summary || '',
      createdDate: new Date(),
      createdBy: 'Current User'
    };

    // Extract and convert clauses
    const clauses: ExtendedReviewClause[] = extractClauses(claudeAnalysis);

    return { summary, clauses };
  };

  // Helper functions to extract specific information
  const extractContractValue = (analysis: any): number => {
    // Try to extract from various possible locations in the analysis
    if (analysis.contractValue) return analysis.contractValue;
    if (analysis.financialTerms?.totalValue) return analysis.financialTerms.totalValue;
    
    // Try to parse from summary or key terms
    const valuePattern = /\$[\d,]+(\.\d{2})?|\d+\s*(million|thousand|k|m)/i;
    const summaryMatch = analysis.summary?.match(valuePattern);
    if (summaryMatch) {
      return parseMonetaryValue(summaryMatch[0]);
    }
    
    return 0;
  };

  const extractDuration = (analysis: any): string => {
    if (analysis.duration) return analysis.duration;
    if (analysis.term) return analysis.term;
    
    // Try to extract from dates
    if (analysis.startDate && analysis.endDate) {
      const start = new Date(analysis.startDate);
      const end = new Date(analysis.endDate);
      const months = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30));
      return `${months} months`;
    }
    
    return 'Not specified';
  };

  const extractParties = (analysis: any): string[] => {
    const parties: string[] = [];
    if (analysis.parties) return analysis.parties;
    if (analysis.buyer) parties.push(analysis.buyer);
    if (analysis.supplier) parties.push(analysis.supplier);
    if (analysis.vendor) parties.push(analysis.vendor);
    return parties;
  };

  const extractObligations = (analysis: any): string[] => {
    if (analysis.obligations) return analysis.obligations;
    if (analysis.keyObligations) return analysis.keyObligations;
    if (analysis.deliverables) return analysis.deliverables;
    return [];
  };

  const extractCriticalDates = (analysis: any): string[] => {
    const dates: string[] = [];
    if (analysis.criticalDates) return analysis.criticalDates;
    if (analysis.milestones) {
      analysis.milestones.forEach((m: any) => {
        if (m.date) dates.push(`${m.name}: ${m.date}`);
      });
    }
    return dates;
  };

  const extractClauses = (analysis: any): ExtendedReviewClause[] => {
    const clauses: ExtendedReviewClause[] = [];
    
    // Process negotiation points if available
    if (analysis.negotiationStrategy?.suggestions) {
      analysis.negotiationStrategy.suggestions.forEach((suggestion: any, index: number) => {
        clauses.push({
          clauseId: `clause_${contractId}_${Date.now()}_${index}`,
          clauseTitle: suggestion.currentTerm || `Clause ${index + 1}`,
          clauseContent: suggestion.currentTerm || '',
          clauseText: suggestion.currentTerm || '',
          category: mapToClauseCategory(suggestion.category || 'Other'),
          priority: mapToPriority(suggestion.priority),
          status: 'pending',
          currentStatus: 'pending',
          comments: suggestion.rationale || '',
          reviewedBy: 'AI Analysis',
          reviewDate: new Date(),
          proposedChanges: suggestion.suggestedChange || '',
          riskLevel: mapToPriority(suggestion.priority),
          notes: suggestion.rationale || '',
          isAutoExtracted: true,
          negotiationHistory: []
        });
      });
    }
    
    // Process unfavorable terms
    if (analysis.unfavorableTerms) {
      analysis.unfavorableTerms.forEach((term: any, index: number) => {
        const existingClause = clauses.find(c => c.clauseTitle === term.clause);
        if (!existingClause) {
          clauses.push({
            clauseId: `clause_${contractId}_${Date.now()}_unfav_${index}`,
            clauseTitle: term.clause || `Unfavorable Term ${index + 1}`,
            clauseContent: term.issue || '',
            clauseText: term.issue || '',
            category: mapToClauseCategory(term.category || 'Other'),
            priority: 'high',
            status: 'pending',
            currentStatus: 'pending',
            comments: term.recommendation || '',
            reviewedBy: 'AI Analysis',
            reviewDate: new Date(),
            proposedChanges: term.recommendation || '',
            riskLevel: 'high',
            notes: `Unfavorable: ${term.issue}`,
            isAutoExtracted: true,
            negotiationHistory: []
          });
        }
      });
    }
    
    return clauses;
  };

  // Map to valid ClauseCategory
  const mapToClauseCategory = (category: string): ClauseCategory => {
    const categoryMap: { [key: string]: ClauseCategory } = {
      'pricing': 'Pricing',
      'payment': 'Payment',
      'payment terms': 'Payment',
      'terms': 'Terms',
      'delivery': 'Terms',
      'warranties': 'Warranty',
      'warranty': 'Warranty',
      'termination': 'Termination',
      'liability': 'Liability',
      'ip': 'Intellectual Property',
      'intellectual property': 'Intellectual Property',
      'confidentiality': 'Confidentiality',
      'indemnification': 'Other',
      'force majeure': 'Force Majeure'
    };
    return categoryMap[category.toLowerCase()] || 'Other';
  };

  // Map to Priority type
  const mapToPriority = (priority: string): Priority => {
    const p = priority?.toLowerCase();
    if (p === 'high' || p === 'critical') return 'high';
    if (p === 'low') return 'low';
    return 'medium';
  };

  // Parse monetary values
  const parseMonetaryValue = (value: string): number => {
    const cleanValue = value.replace(/[$,]/g, '');
    const num = parseFloat(cleanValue);
    if (value.toLowerCase().includes('million') || value.toLowerCase().includes('m')) {
      return num * 1000000;
    }
    if (value.toLowerCase().includes('thousand') || value.toLowerCase().includes('k')) {
      return num * 1000;
    }
    return num;
  };

  const handleAddClause = () => {
    if (!newClauseForm.clauseTitle || !newClauseForm.clauseText) {
      alert('Please fill in clause title and text.');
      return;
    }

    const newClause: ExtendedReviewClause = {
      clauseId: `clause_${contractId}_${Date.now()}_manual`,
      clauseTitle: newClauseForm.clauseTitle,
      clauseContent: newClauseForm.clauseText,
      clauseText: newClauseForm.clauseText,
      category: newClauseForm.category as ClauseCategory,
      status: newClauseForm.currentStatus as ClauseStatus,
      currentStatus: newClauseForm.currentStatus as ClauseStatus,
      priority: newClauseForm.priority as Priority,
      proposedChanges: newClauseForm.proposedChanges || '',
      riskLevel: newClauseForm.riskLevel as Priority,
      reviewedBy: 'Current User',
      reviewDate: new Date(),
      notes: newClauseForm.notes || '',
      comments: newClauseForm.notes || '',
      isAutoExtracted: false,
      negotiationHistory: []
    };

    setClauses([...clauses, newClause]);
    
    // Update contract
    const updatedReviewClauses = [...(contract?.reviewClauses || []), {
      clauseId: newClause.clauseId,
      clauseTitle: newClause.clauseTitle,
      clauseContent: newClause.clauseContent,
      category: newClause.category,
      priority: newClause.priority,
      status: newClause.status,
      comments: newClause.comments,
      reviewedBy: newClause.reviewedBy,
      reviewDate: newClause.reviewDate,
      negotiationHistory: newClause.negotiationHistory
    }];
    
    onUpdateContract(contractId, { reviewClauses: updatedReviewClauses });
    
    // Reset form
    setNewClauseForm({
      clauseTitle: '',
      clauseText: '',
      category: 'Terms',
      currentStatus: 'pending',
      priority: 'medium',
      proposedChanges: '',
      riskLevel: 'medium',
      notes: '',
      isAutoExtracted: false
    });
    setShowAddClauseForm(false);
  };

  const handleSaveSummary = () => {
    const contractSummary: ContractSummary = {
      generatedSummary: summaryForm.executiveSummary,
      keyFindings: summaryForm.keyFindings,
      riskLevel: summaryForm.riskLevel,
      createdDate: new Date(),
      createdBy: 'Current User'
    };
    
    onUpdateContract(contractId, { contractSummary });
    alert('Summary saved successfully!');
  };

  const handleSaveClauses = () => {
    const reviewClauses: ReviewClause[] = clauses.map(clause => ({
      clauseId: clause.clauseId,
      clauseTitle: clause.clauseTitle,
      clauseContent: clause.clauseContent,
      category: clause.category,
      priority: clause.priority,
      status: clause.status,
      comments: clause.comments,
      reviewedBy: clause.reviewedBy,
      reviewDate: clause.reviewDate,
      negotiationHistory: clause.negotiationHistory
    }));
    
    onUpdateContract(contractId, { reviewClauses });
    alert('Clauses saved successfully!');
  };

  // Helper functions for UI
  const getStatusColor = (status: ClauseStatus) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'agreed': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'compromised': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: Priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-blue-100 text-blue-800'
    };
    return colors[priority];
  };

  if (!contract) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Contract not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/contracts')}
                className="text-gray-400 hover:text-gray-600"
              >
                ← Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Contract Review</h1>
                <p className="text-sm text-gray-600">{decodeHtmlEntities(contract.contractTitle)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                Supplier: {contract.supplier}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow-sm rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('upload')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'upload'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Upload className="w-4 h-4 inline-block mr-2" />
                Upload Analysis
              </button>
              <button
                onClick={() => setActiveTab('summary')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'summary'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4 inline-block mr-2" />
                Summary
              </button>
              <button
                onClick={() => setActiveTab('clauses')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'clauses'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <CheckCircle className="w-4 h-4 inline-block mr-2" />
                Clauses ({clauses.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Upload Contract Analysis Document</h2>
                <p className="text-sm text-gray-600 mb-6">
                  Upload a pre-made contract analysis document to automatically extract summary and clause information.
                </p>
              </div>

              {!uploadedFile ? (
                <div className="max-w-xl mx-auto">
                  <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-10 h-10 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PDF, DOC, DOCX, TXT, or MD (MAX. 10MB)</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.txt,.md"
                    />
                  </label>
                </div>
              ) : (
                <div className="max-w-xl mx-auto space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{uploadedFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setUploadedFile(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {isProcessing ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Processing with AI...</span>
                        <span className="text-gray-900 font-medium">{processingProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${processingProgress}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 text-center">
                        {processingProgress < 30 && 'Reading document...'}
                        {processingProgress >= 30 && processingProgress < 80 && 'Analyzing with Claude AI...'}
                        {processingProgress >= 80 && 'Finalizing results...'}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={processUploadedFile}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Brain className="w-4 h-4" />
                      Process with AI
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-gray-900">Contract Summary</h2>
                <button
                  onClick={handleSaveSummary}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Save Summary
                </button>
              </div>

              {/* Editable Summary Form */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Executive Summary
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    value={summaryForm.executiveSummary}
                    onChange={(e) => setSummaryForm({ ...summaryForm, executiveSummary: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contract Value
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={summaryForm.contractValue}
                        onChange={(e) => setSummaryForm({ ...summaryForm, contractValue: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contract Duration
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={summaryForm.contractDuration}
                      onChange={(e) => setSummaryForm({ ...summaryForm, contractDuration: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Risk Level
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={summaryForm.riskLevel}
                    onChange={(e) => setSummaryForm({ ...summaryForm, riskLevel: e.target.value as 'high' | 'medium' | 'low' })}
                  >
                    <option value="low">Low Risk</option>
                    <option value="medium">Medium Risk</option>
                    <option value="high">High Risk</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key Findings
                  </label>
                  <div className="space-y-2">
                    {summaryForm.keyFindings.map((finding, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={finding}
                          onChange={(e) => {
                            const newFindings = [...summaryForm.keyFindings];
                            newFindings[index] = e.target.value;
                            setSummaryForm({ ...summaryForm, keyFindings: newFindings });
                          }}
                        />
                        <button
                          onClick={() => {
                            const newFindings = summaryForm.keyFindings.filter((_, i) => i !== index);
                            setSummaryForm({ ...summaryForm, keyFindings: newFindings });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setSummaryForm({ ...summaryForm, keyFindings: [...summaryForm.keyFindings, ''] })}
                      className="text-blue-600 hover:text-blue-700 text-sm"
                    >
                      + Add Finding
                    </button>
                  </div>
                </div>
              </div>

              {/* Summary Display */}
              {summaryForm.executiveSummary && (
                <div className="mt-8 bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Summary Preview</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-700 leading-relaxed">{summaryForm.executiveSummary}</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-xs text-gray-500">Contract Value</p>
                        <p className="text-lg font-semibold text-gray-900">
                          ${summaryForm.contractValue.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-xs text-gray-500">Duration</p>
                        <p className="text-lg font-semibold text-gray-900">{summaryForm.contractDuration}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-xs text-gray-500">Risk Level</p>
                        <p className={`text-lg font-semibold ${
                          summaryForm.riskLevel === 'high' ? 'text-red-600' :
                          summaryForm.riskLevel === 'medium' ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {summaryForm.riskLevel.charAt(0).toUpperCase() + summaryForm.riskLevel.slice(1)}
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-xs text-gray-500">Key Findings</p>
                        <p className="text-lg font-semibold text-gray-900">{summaryForm.keyFindings.length}</p>
                      </div>
                    </div>

                    {summaryForm.keyFindings.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Key Findings</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {summaryForm.keyFindings.map((finding, index) => (
                            <li key={index} className="text-sm text-gray-600">{finding}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Clauses Tab */}
          {activeTab === 'clauses' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-gray-900">Review Clauses</h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowAddClauseForm(!showAddClauseForm)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Clause
                  </button>
                  <button
                    onClick={handleSaveClauses}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save All
                  </button>
                </div>
              </div>

              {/* Clause List */}
              <div className="space-y-4">
                {clauses.map((clause) => (
                  <div
                    key={clause.clauseId}
                    className={`border rounded-lg overflow-hidden ${
                      clause.isAutoExtracted ? 'border-blue-200' : 'border-gray-200'
                    }`}
                  >
                    <div
                      className={`p-4 ${
                        clause.isAutoExtracted ? 'bg-blue-50' : 'bg-gray-50'
                      }`}
                    >
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-medium text-gray-900">{clause.clauseTitle}</h3>
                            {clause.isAutoExtracted && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                <Brain className="w-3 h-3 mr-1" />
                                Auto-extracted
                              </span>
                            )}
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(clause.currentStatus)}`}>
                              {clause.currentStatus}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(clause.priority)}`}>
                              {clause.priority} priority
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {clause.category}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(clause.reviewDate).toLocaleDateString()}
                            </span>
                            {clause.riskLevel && (
                              <span className="flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {clause.riskLevel} risk
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Expandable Details */}
                        <details className="group">
                          <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-700">
                            View Details
                          </summary>
                          <div className="mt-3 space-y-3">
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Clause Text</h4>
                              <p className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                                {clause.clauseText}
                              </p>
                            </div>

                            {clause.proposedChanges && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">Proposed Changes</h4>
                                <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-200">
                                  {clause.proposedChanges}
                                </p>
                              </div>
                            )}

                            {clause.notes && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">Notes</h4>
                                <p className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                                  {clause.notes}
                                </p>
                              </div>
                            )}

                            <div className="flex items-center gap-3 pt-3">
                              <select
                                value={clause.currentStatus}
                                onChange={(e) => {
                                  const updatedClauses = clauses.map(c =>
                                    c.clauseId === clause.clauseId
                                      ? { ...c, currentStatus: e.target.value as ClauseStatus }
                                      : c
                                  );
                                  setClauses(updatedClauses);
                                }}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="pending">Pending</option>
                                <option value="agreed">Agreed</option>
                                <option value="rejected">Rejected</option>
                                <option value="compromised">Compromised</option>
                              </select>
                              <button
                                onClick={() => {
                                  const filteredClauses = clauses.filter(c => c.clauseId !== clause.clauseId);
                                  setClauses(filteredClauses);
                                }}
                                className="text-sm text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </details>
                      </div>
                    </div>
                  </div>
                ))}

                {clauses.length === 0 && (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No clauses added yet</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Upload an analysis document or add clauses manually
                    </p>
                  </div>
                )}
              </div>

              {/* Add Clause Form */}
              {showAddClauseForm && (
                <div className="mt-6 bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Clause</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Clause Title
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={newClauseForm.clauseTitle}
                        onChange={(e) => setNewClauseForm({ ...newClauseForm, clauseTitle: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Clause Text
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={4}
                        value={newClauseForm.clauseText}
                        onChange={(e) => setNewClauseForm({ ...newClauseForm, clauseText: e.target.value })}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={newClauseForm.category}
                          onChange={(e) => setNewClauseForm({ ...newClauseForm, category: e.target.value as ClauseCategory })}
                        >
                          <option value="Pricing">Pricing</option>
                          <option value="Terms">Terms</option>
                          <option value="Liability">Liability</option>
                          <option value="Payment">Payment</option>
                          <option value="Termination">Termination</option>
                          <option value="Warranty">Warranty</option>
                          <option value="Confidentiality">Confidentiality</option>
                          <option value="Intellectual Property">Intellectual Property</option>
                          <option value="Force Majeure">Force Majeure</option>
                          <option value="Dispute Resolution">Dispute Resolution</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Priority
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={newClauseForm.priority}
                          onChange={(e) => setNewClauseForm({ ...newClauseForm, priority: e.target.value as Priority })}
                        >
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Risk Level
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={newClauseForm.riskLevel}
                          onChange={(e) => setNewClauseForm({ ...newClauseForm, riskLevel: e.target.value as Priority })}
                        >
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proposed Changes
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        value={newClauseForm.proposedChanges}
                        onChange={(e) => setNewClauseForm({ ...newClauseForm, proposedChanges: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                        value={newClauseForm.notes}
                        onChange={(e) => setNewClauseForm({ ...newClauseForm, notes: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-6">
                    <button
                      onClick={handleAddClause}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Add Clause
                    </button>
                    <button
                      onClick={() => setShowAddClauseForm(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractReviewPage; 